import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PaymentRequestButton } from 'modules/purchase/components/PaymentRequestButton'
import { eventLogger } from 'services/eventLogger.service'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectActionList,
  selectCurrentVariantCohort,
  selectScreenName,
} from 'root-redux/selects/common'
import { createProductId } from 'helpers/createProductId'
import {
  select3DSecureIframeUrl,
  selectCurrency,
  selectSubscriptionCurrentPrice,
  selectSubscriptionDiscountAmount,
  selectSubscriptionOldPrice,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodPrice,
} from 'modules/purchase/redux/selects'
import { CHECK_PAYMENT_REQUEST_BUTTON } from 'modules/purchase/redux/actions'
import securitySystems from 'assets/images/security-systems.png'
import { Spinner } from 'components/Spinner'
import { resetErrorAction } from 'root-redux/actions/common'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { useLocation } from 'react-router'
import { PaymentWaitingModal } from 'modules/purchase/components/PaymentWaitingModal'
import { PayPalButton } from 'modules/purchase/components/PayPalButton'
import { CreditCardForm } from 'modules/purchase/components/CreditCardForm'
import {
  CURRENCY_SYMBOLS,
  ONE_HUNDRED_PERCENTS,
} from 'modules/purchase/constants'
import { Separator } from 'modules/purchase/components/Separator'
import { StyledCheckout as S } from './Checkout.styles'

type TProps = {
  setIsCheckoutShown: (value: boolean) => void
}

export const Checkout: React.FC<TProps> = ({ setIsCheckoutShown }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const creditCardPaymentRef = useRef<HTMLDivElement>(null)

  const cohort = useSelector(selectCurrentVariantCohort)
  const periodName = useSelector(selectSubscriptionPeriodName)
  const periodQuantity = useSelector(selectSubscriptionPeriodQuantity)
  const price = useSelector(selectSubscriptionCurrentPrice)
  const trialCurrentPrice = useSelector(selectSubscriptionTrialPeriodPrice)
  const oldPrice = useSelector(selectSubscriptionOldPrice)
  const discountAmount = useSelector(selectSubscriptionDiscountAmount)
  const threeDSecureIframeUrl = useSelector(select3DSecureIframeUrl)
  const fetchingActionsList = useSelector(selectActionList)
  const screenName = useSelector(selectScreenName)
  const currency = useSelector(selectCurrency)

  const [isPaymentWaitingShown, setIsPaymentWaitingShown] = useState(false)

  const productId = useMemo(
    () =>
      createProductId({
        periodName,
        periodQuantity,
        price: trialCurrentPrice || price,
      }),
    [periodName, periodQuantity, trialCurrentPrice, price],
  )

  const isCheckoutReady = useMemo(
    () => !fetchingActionsList.includes(CHECK_PAYMENT_REQUEST_BUTTON),
    [fetchingActionsList],
  )

  const percentDiscount = useMemo(
    () => Math.round((discountAmount / oldPrice) * ONE_HUNDRED_PERCENTS),
    [oldPrice, discountAmount],
  )

  useEffect(() => {
    if (productId) {
      eventLogger.logPurchaseShown({
        productId,
        screenName,
      })
    }
  }, [productId, screenName])

  useEffect(() => {
    const { current: elem } = creditCardPaymentRef
    if (elem) {
      const yCoordinate = elem.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: yCoordinate, behavior: 'smooth' })
    }
  }, [])

  const handleCloseCheckout = useCallback(() => {
    dispatch(resetErrorAction())
    setIsCheckoutShown(false)

    eventLogger.logPurchaseScreenClosed({
      productId,
      screenName,
    })
    googleAnalyticsLogger.logPageView(`${pathname}/${cohort}`)
  }, [dispatch, setIsCheckoutShown, pathname, cohort, productId, screenName])

  return (
    <div>
      {threeDSecureIframeUrl ? (
        <S.ThreeDSecureIframe title="3DSecure" src={threeDSecureIframeUrl} />
      ) : (
        <>
          {isCheckoutReady && <S.CloseButton onClick={handleCloseCheckout} />}
          <S.Wrapper isVisible={isCheckoutReady}>
            <S.Title>{t`purchase.checkout.title`}</S.Title>
            <div>
              <S.DescriptionContainer>
                <S.PersonalizedPlan>
                  <Trans
                    i18nKey="purchase.checkout.personalizedPlan"
                    components={[<br />]}
                  />
                </S.PersonalizedPlan>
                <S.PlanPrice>
                  <Trans
                    i18nKey="purchase.checkout.oldPrice"
                    values={{
                      oldPrice,
                      currency: CURRENCY_SYMBOLS[currency],
                    }}
                  />
                </S.PlanPrice>
              </S.DescriptionContainer>
              <S.DescriptionContainer>
                <span>
                  {t('purchase.checkout.offerDiscount', { percentDiscount })}
                </span>
                <S.Discount>
                  -
                  <Trans
                    i18nKey="purchase.checkout.discountAmount"
                    values={{
                      discountAmount,
                      currency: CURRENCY_SYMBOLS[currency],
                    }}
                  />
                </S.Discount>
              </S.DescriptionContainer>
            </div>
            <S.TotalAmountContainer>
              <span>{t`purchase.checkout.total`}</span>
              <span>
                <Trans
                  i18nKey="purchase.checkout.price"
                  values={{
                    price: trialCurrentPrice || price,
                    currency: CURRENCY_SYMBOLS[currency],
                  }}
                />
              </span>
            </S.TotalAmountContainer>
            <S.DiscountDescription>
              {t('purchase.checkout.discountDescription', {
                percentDiscount,
                discountAmount,
                currency: CURRENCY_SYMBOLS[currency],
              })}
            </S.DiscountDescription>
            <S.PaymentContainer>
              <div ref={creditCardPaymentRef}>
                <CreditCardForm />
              </div>
              <Separator />
              <PayPalButton />
              <S.ButtonContainer>
                <PaymentRequestButton />
              </S.ButtonContainer>

              <S.Image src={securitySystems} alt="security-systems" />
              <S.Text>{t`purchase.checkout.moneyBackGuarantee`}</S.Text>
            </S.PaymentContainer>
          </S.Wrapper>
          <PaymentWaitingModal
            isPaymentWaitingShown={isPaymentWaitingShown}
            setIsPaymentWaitingShown={setIsPaymentWaitingShown}
          />
          {!isCheckoutReady && <Spinner />}
        </>
      )}
    </div>
  )
}
